import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { messages, type Locale, type Messages } from './messages';

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  m: Messages;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function resolveInitialLocale(search = window.location.search): Locale {
  const query = new URLSearchParams(search).get('lang');
  if (query === 'it' || query === 'en') return query;
  const stored = window.localStorage.getItem('orivon_locale');
  if (stored === 'it' || stored === 'en') return stored;
  return navigator.language.toLowerCase().startsWith('it') ? 'it' : 'en';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => resolveInitialLocale());

  const setLocale = (next: Locale) => {
    window.localStorage.setItem('orivon_locale', next);
    setLocaleState(next);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title =
      locale === 'it'
        ? 'Orivon — Il tuo sistema operativo professionale'
        : 'Orivon — Your professional operating system';
    window.localStorage.setItem('orivon_locale', locale);
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, m: messages[locale] }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside I18nProvider');
  return value;
}
